import { useState, useEffect, useMemo } from "react";
import { useAuth, ROLES_FULL_ACCESS } from "../App";
import { ClickableUser } from "../contexts/UserSheetContext";
import axios from "axios";
import { API } from "../App";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import {
  Plus,
  Calendar as CalendarIcon,
  CheckCircle2,
  XCircle,
  Trash2,
  Loader2,
  Filter,
  Pencil
} from "lucide-react";

const getInitials = (name) => {
  const parts = (name || "").split(" ");
  return parts.map((p) => p[0]).join("").slice(0, 2).toUpperCase();
};

const LeavesPage = () => {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");

  // New leave form state
  const [leaveType, setLeaveType] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reason, setReason] = useState("");

  // Edit leave state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editLeave, setEditLeave] = useState(null);
  const [editLeaveType, setEditLeaveType] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editReason, setEditReason] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const usersMap = useMemo(
    () => Object.fromEntries(allUsers.map((u) => [u.id, u])),
    [allUsers]
  );

  useEffect(() => {
    fetchLeaves();
    if (!["employee", "collaborateur"].includes(user?.role)) {
      axios.get(`${API}/users`).then((r) => setAllUsers(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeaves = async () => {
    try {
      const response = await axios.get(`${API}/leaves`);
      setLeaves(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des congés");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate || !leaveType) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leaves`, {
        leave_type: leaveType,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        reason: reason
      });
      toast.success("Demande de congé envoyée !");
      setIsDialogOpen(false);
      resetForm();
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      await axios.put(`${API}/leaves/${leaveId}/review?action=approved`);
      toast.success("Congé approuvé");
      fetchLeaves();
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
    }
  };

  const handleReject = async (leaveId) => {
    try {
      await axios.put(`${API}/leaves/${leaveId}/review?action=rejected`);
      toast.success("Congé refusé");
      fetchLeaves();
    } catch (error) {
      toast.error("Erreur lors du refus");
    }
  };

  const handleDelete = async (leaveId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette demande ?")) return;

    try {
      await axios.delete(`${API}/leaves/${leaveId}`);
      toast.success("Demande supprimée");
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleEditOpen = (leave) => {
    setEditLeave(leave);
    setEditLeaveType(leave.leave_type);
    setEditStartDate(leave.start_date);
    setEditEndDate(leave.end_date);
    setEditReason(leave.reason || "");
    setEditStatus(leave.status);
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editLeave) return;
    setIsEditSubmitting(true);
    try {
      await axios.put(`${API}/leaves/${editLeave.id}`, {
        leave_type: editLeaveType,
        start_date: editStartDate,
        end_date: editEndDate,
        reason: editReason,
        status: editStatus
      });
      toast.success("Congé modifié avec succès !");
      setIsEditDialogOpen(false);
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const resetForm = () => {
    setLeaveType("");
    setStartDate(null);
    setEndDate(null);
    setReason("");
  };

  const getLeaveTypeLabel = (type) => {
    const labels = {
      conge_paye: "Congé payé",
      maladie: "Maladie",
      sans_solde: "Sans solde",
      rtt: "RTT",
      autre: "Autre"
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: "En attente", className: "badge-pending border" },
      approved: { label: "Approuvé", className: "badge-approved border" },
      rejected: { label: "Refusé", className: "badge-rejected border" }
    };
    const { label, className } = config[status] || config.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  const filteredLeaves = leaves.filter(leave => {
    if (filterStatus === "all") return true;
    return leave.status === filterStatus;
  });

  const canManageLeave = (leave) => {
    if (ROLES_FULL_ACCESS.includes(user?.role)) return leave.status === "pending";
    return user?.role === "manager" && leave.status === "pending";
  };

  const canEditLeave = () => ROLES_FULL_ACCESS.includes(user?.role);

  const canDeleteLeave = (leave) => {
    if (ROLES_FULL_ACCESS.includes(user?.role)) return true;
    return leave.user_id === user?.id && leave.status === "pending";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E] dark:text-[#4B8BF4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leaves-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-xl md:text-3xl font-bold text-[#111827] dark:text-[#D6E4F5]">
            Gestion des congés
          </h1>
          <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1 text-sm md:text-base">
            {user?.role === "employee"
              ? "Consultez et gérez vos demandes de congés"
              : "Gérez les demandes de congés de votre équipe"}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]" data-testid="filter-status">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="approved">Approuvés</SelectItem>
              <SelectItem value="rejected">Refusés</SelectItem>
            </SelectContent>
          </Select>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal={true}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d] rounded-full"
                data-testid="new-leave-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle demande
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md z-50 max-h-[90vh] overflow-y-auto dark:bg-[#0B1628] dark:border-[#1A3050]">
              <DialogHeader>
                <DialogTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
                  Demande de congé
                </DialogTitle>
                <DialogDescription className="dark:text-[#7EA8CE]">
                  Remplissez le formulaire pour soumettre une demande de congé.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="leaveType" className="dark:text-[#D6E4F5]">Type de congé *</Label>
                  <Select value={leaveType} onValueChange={setLeaveType}>
                    <SelectTrigger data-testid="leave-type-select" className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                      <SelectValue placeholder="Sélectionnez un type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conge_paye">Congé payé</SelectItem>
                      <SelectItem value="maladie">Maladie</SelectItem>
                      <SelectItem value="sans_solde">Sans solde</SelectItem>
                      <SelectItem value="rtt">RTT</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="dark:text-[#D6E4F5]">Date de début *</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                          data-testid="start-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, "dd/MM/yyyy") : "Sélectionner"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[100]" align="start">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          locale={fr}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label className="dark:text-[#D6E4F5]">Date de fin *</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                          data-testid="end-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, "dd/MM/yyyy") : "Sélectionner"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[100]" align="start">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          locale={fr}
                          autoFocus
                          disabled={(date) => startDate && date < startDate}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason" className="dark:text-[#D6E4F5]">Motif (optionnel)</Label>
                  <Textarea
                    id="reason"
                    placeholder="Précisez le motif de votre demande..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                    data-testid="leave-reason"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="dark:border-[#1A3050] dark:text-[#D6E4F5] dark:hover:bg-[#112240]"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d]"
                    disabled={isSubmitting}
                    data-testid="submit-leave"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Soumettre"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen} modal={true}>
        <DialogContent className="sm:max-w-md z-50 max-h-[90vh] overflow-y-auto dark:bg-[#0B1628] dark:border-[#1A3050]">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
              Modifier le congé
            </DialogTitle>
            <DialogDescription className="dark:text-[#7EA8CE]">
              Modifiez les informations du congé de {editLeave?.user_name}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="dark:text-[#D6E4F5]">Type de congé</Label>
              <Select value={editLeaveType} onValueChange={setEditLeaveType}>
                <SelectTrigger className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="conge_paye">Congé payé</SelectItem>
                  <SelectItem value="maladie">Maladie</SelectItem>
                  <SelectItem value="sans_solde">Sans solde</SelectItem>
                  <SelectItem value="rtt">RTT</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="dark:text-[#D6E4F5]">Date de début</Label>
                <Input
                  type="date"
                  value={editStartDate}
                  onChange={(e) => setEditStartDate(e.target.value)}
                  className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                />
              </div>
              <div className="space-y-2">
                <Label className="dark:text-[#D6E4F5]">Date de fin</Label>
                <Input
                  type="date"
                  value={editEndDate}
                  onChange={(e) => setEditEndDate(e.target.value)}
                  className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="dark:text-[#D6E4F5]">Statut</Label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="approved">Approuvé</SelectItem>
                  <SelectItem value="rejected">Refusé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="dark:text-[#D6E4F5]">Motif</Label>
              <Textarea
                placeholder="Motif..."
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
                className="dark:border-[#1A3050] dark:text-[#D6E4F5] dark:hover:bg-[#112240]"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d]"
                disabled={isEditSubmitting}
              >
                {isEditSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Enregistrer"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Leaves Table */}
      <Card className="card-neumorphic">
        <CardContent className="p-0">
          {filteredLeaves.length === 0 ? (
            <div className="text-center py-12 text-[#9CA3AF] dark:text-[#3D607E]">
              <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune demande de congé</p>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="sm:hidden divide-y divide-gray-100 dark:divide-[#1A3050]">
                {filteredLeaves.map((leave) => (
                  <div key={leave.id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        {user?.role !== "employee" && user?.role !== "collaborateur" && (() => {
                          const u = usersMap[leave.user_id];
                          return (
                            <Avatar className="w-7 h-7 shrink-0 mt-0.5">
                              <AvatarImage src={u?.avatar_url} />
                              <AvatarFallback className="text-[10px] bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70]">
                                {getInitials(leave.user_name)}
                              </AvatarFallback>
                            </Avatar>
                          );
                        })()}
                      <div className="min-w-0">
                        {user?.role !== "employee" && user?.role !== "collaborateur" && (
                          <p className="font-medium text-sm text-[#111827] dark:text-[#D6E4F5] truncate">
                            <ClickableUser userId={leave.user_id}>{leave.user_name}</ClickableUser>
                          </p>
                        )}
                        <p className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">
                          {getLeaveTypeLabel(leave.leave_type)}
                        </p>
                        <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mt-0.5">
                          {leave.start_date} → {leave.end_date}
                        </p>
                        {leave.reason && (
                          <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mt-0.5 truncate">
                            {leave.reason}
                          </p>
                        )}
                      </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        {getStatusBadge(leave.status)}
                        <div className="flex gap-1">
                          {canEditLeave() && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-blue-600 dark:text-[#4B8BF4] hover:bg-blue-50 dark:hover:bg-[#0D1E3A]"
                              onClick={() => handleEditOpen(leave)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {canManageLeave(leave) && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                                onClick={() => handleApprove(leave.id)}
                                data-testid={`approve-${leave.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                onClick={() => handleReject(leave.id)}
                                data-testid={`reject-${leave.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {canDeleteLeave(leave) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-gray-500 dark:text-[#7EA8CE] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              onClick={() => handleDelete(leave.id)}
                              data-testid={`delete-${leave.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="dark:border-[#1A3050]">
                      {user?.role !== "employee" && user?.role !== "collaborateur" && (
                        <TableHead className="dark:text-[#7EA8CE]">Collaborateur</TableHead>
                      )}
                      <TableHead className="dark:text-[#7EA8CE]">Type</TableHead>
                      <TableHead className="dark:text-[#7EA8CE]">Début</TableHead>
                      <TableHead className="dark:text-[#7EA8CE]">Fin</TableHead>
                      <TableHead className="dark:text-[#7EA8CE]">Statut</TableHead>
                      <TableHead className="dark:text-[#7EA8CE] hidden md:table-cell">Motif</TableHead>
                      <TableHead className="text-right dark:text-[#7EA8CE]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeaves.map((leave) => (
                      <TableRow key={leave.id} className="group dark:border-[#1A3050] dark:hover:bg-[#112240]/50">
                        {user?.role !== "employee" && user?.role !== "collaborateur" && (() => {
                          const u = usersMap[leave.user_id];
                          return (
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="w-7 h-7 shrink-0">
                                  <AvatarImage src={u?.avatar_url} />
                                  <AvatarFallback className="text-[10px] bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70]">
                                    {getInitials(leave.user_name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium dark:text-[#D6E4F5] whitespace-nowrap"><ClickableUser userId={leave.user_id}>{leave.user_name}</ClickableUser></span>
                              </div>
                            </TableCell>
                          );
                        })()}
                        <TableCell className="dark:text-[#D6E4F5]">
                          <span className="whitespace-nowrap">{getLeaveTypeLabel(leave.leave_type)}</span>
                        </TableCell>
                        <TableCell className="dark:text-[#D6E4F5]">{leave.start_date}</TableCell>
                        <TableCell className="dark:text-[#D6E4F5]">{leave.end_date}</TableCell>
                        <TableCell>{getStatusBadge(leave.status)}</TableCell>
                        <TableCell className="max-w-[200px] truncate dark:text-[#7EA8CE] hidden md:table-cell">
                          {leave.reason || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {canEditLeave() && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-blue-600 dark:text-[#4B8BF4] hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-[#0D1E3A]"
                                onClick={() => handleEditOpen(leave)}
                                title="Modifier"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {canManageLeave(leave) && (
                              <>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                                  onClick={() => handleApprove(leave.id)}
                                  data-testid={`approve-${leave.id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  onClick={() => handleReject(leave.id)}
                                  data-testid={`reject-${leave.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {canDeleteLeave(leave) && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-gray-500 dark:text-[#7EA8CE] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                onClick={() => handleDelete(leave.id)}
                                data-testid={`delete-${leave.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LeavesPage;
